import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSubscription?: boolean;
  requirePlan?: "sentinela" | "premium" | "ultra";
}

export function ProtectedRoute({ 
  children, 
  requireSubscription = false,
  requirePlan
}: ProtectedRouteProps) {
  const { user, session, loading, subscription } = useAuth();
  const location = useLocation();

  // While loading auth state, show spinner
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // CRITICAL: User must be authenticated with a valid session
  // Check both user AND session to prevent race conditions
  if (!user || !session) {
    // Redirect to auth with return URL for better UX
    const returnUrl = location.pathname + location.search;
    return <Navigate to={`/auth?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }

  if (requireSubscription && !subscription.subscribed) {
    return <Navigate to="/planos" replace />;
  }

  // Check specific plan requirement
  if (requirePlan) {
    const planHierarchy = { sentinela: 1, premium: 2, ultra: 3 };
    const requiredLevel = planHierarchy[requirePlan];
    const userLevel = planHierarchy[subscription.planTier as keyof typeof planHierarchy] || 0;
    
    if (!subscription.subscribed || userLevel < requiredLevel) {
      return <Navigate to="/planos" replace />;
    }
  }

  return <>{children}</>;
}